/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import React, {
  ComponentProps,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { Text } from '../../components/Text';
import { Tooltip } from '../../components/Tooltip';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { Fade } from '../Fade';
import { Flex } from '../Flex';
import { CloseReason, Overlay } from '../Overlay';
import { Pane } from '../Pane';
import { RestrictedButton } from '../RestrictedButton';
import css from './index.module.scss';

export interface ModalProps {
  testId?: string;
  /** Called when a user action signifies intent to close the modal */
  onCloseSignal: () => void;

  /** Called after the modal has fully closed. If any action is being
   * taken at close time, it's important to use onCloseSignal to avoid
   * an unnecessary delay awaiting the end of the modal close transition
   * before kicking off that action.
   */
  onClosed?: () => void;

  /**
   * Determines the size of the modal.
   */
  size?: 'small' | 'medium' | 'large' | 'x-large';

  /** When true, prevent the Modal from closing on backdrop clicks (force explicit close/cancel) */
  disableBackdropClick?: boolean;

  /**
   * Controls the open state of the modal. Defaults to `true`.
   * @todo This prop should be required, but doing so would require sweeping changes to the app.
   * Make required when all modals have been updated to use the prop correctly.
   */
  open?: boolean;

  /**
   * When true, the modal will take up the full max size. When false, modal will be able to flex with the content up to the max size.
   */
  fullWidth?: boolean;

  /**
   * Additional class name to apply to the modal.
   */
  className?: string;
}

/**
 * Modals are used to show content on top of an overlay.
 */
const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  testId = 'modal',
  onCloseSignal,
  onClosed,
  size = 'medium',
  disableBackdropClick,
  children,
  open = true,
  fullWidth = true,
  className,
  ...rest
}) => {
  const [stableFeedback, setStableFeedback] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (stableFeedback) {
      setTimeout(() => setStableFeedback(false), 1000);
    }
  }, [stableFeedback]);

  const onClose = useCallback(
    (reason: CloseReason) => {
      if (disableBackdropClick && reason === 'mask') setStableFeedback(true);
      else onCloseSignal();
    },
    [disableBackdropClick, onCloseSignal],
  );

  return (
    <Overlay
      data-component="Modal"
      testId={`${testId}-overlay`}
      open={open}
      onClose={onClose}
      onClosed={onClosed}
      {...identifyComponent('modal')}
    >
      <Fade in={open} nodeRef={nodeRef}>
        <Flex justifyContent="center" className={css.container} role="dialog" ref={nodeRef}>
          <Pane
            testId={testId}
            backgroundColor="default"
            borderRadius={6}
            elevation={3}
            height="100%"
            className={classNames(
              css.modal,
              css[size],
              {
                [css.stable]: !!disableBackdropClick,
                [css.stableFeedback]: stableFeedback,
                [css.fullWidth]: fullWidth,
              },
              className,
            )}
            {...rest}
          >
            {children}
          </Pane>
        </Flex>
      </Fade>
    </Overlay>
  );
};

export interface ModalHeaderProps {
  testId?: string;
  onCloseSignal?: React.ComponentProps<typeof Modal>['onCloseSignal'];
  disableDivider?: boolean;
}

export const ModalHeader: React.FC<PropsWithChildren<ModalHeaderProps>> = ({
  testId = 'modal-header',
  onCloseSignal,
  disableDivider,
  children,
}) => {
  const intl = useIntl();
  const closeButton = onCloseSignal ? (
    <IconButton
      iconName="close"
      title={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahO' })}
      onClick={onCloseSignal}
      size="small"
    />
  ) : null;
  return (
    <Pane
      data-component="ModalHeader"
      padding={[0, 16, 0, 16]}
      borderBottom={disableDivider ? undefined : 'standard'}
      height={48}
      className={css.modalHeader}
    >
      <Flex
        testId={testId}
        justifyContent={children ? 'space-between' : 'flex-end'}
        style={{ height: '100%' }}
      >
        {children && (
          <Text appearance="heading3" color="emphasized" noWrap tag="span">
            {children}
          </Text>
        )}
        {closeButton}
      </Flex>
    </Pane>
  );
};

export interface ModalBodyProps {
  testId?: string;
  padding?: ComponentProps<typeof Box>['padding'];
}

export const ModalBody: React.FC<PropsWithChildren<ModalBodyProps>> = ({
  testId = 'modal-body',
  children,
  padding = 20,
}) => (
  <Box data-component="ModalBody" testId={testId} padding={padding} className={css.modalBody}>
    {children}
  </Box>
);

export interface FooterBaseProps
  extends Pick<React.ComponentProps<typeof Pane>, 'backgroundColor'> {
  testId?: string;
  disableDivider?: boolean;
}

export const ModalFooter: React.FC<PropsWithChildren<FooterBaseProps>> = ({
  testId = 'modal-footer',
  disableDivider,
  backgroundColor,
  children,
}) => (
  <Pane
    data-component="ModalFooter"
    testId={testId}
    padding={[12, 16, 12, 16]}
    borderTop={disableDivider ? undefined : 'standard'}
    backgroundColor={backgroundColor}
    borderRadiusBottomLeft={6}
    borderRadiusBottomRight={6}
  >
    {children}
  </Pane>
);

export interface HelpTextFooterProps extends Omit<FooterBaseProps, 'backgroundColor'> {}

export const ModalFooterHelpText: React.FC<PropsWithChildren<HelpTextFooterProps>> = ({
  children,
  ...rest
}) => (
  <ModalFooter data-component="ModalFooterHelpText" backgroundColor="secondary" {...rest}>
    <Text appearance="bodySmall" color="default" tag="span">
      {children}
    </Text>
  </ModalFooter>
);

export interface ModalActionProps {
  action: (e: React.MouseEvent) => void;
  text: string;
  disabled?: boolean;
  disabledTooltip?: string;
  restricted?: boolean;
  restrictedTooltip?: ReactNode;
  restrictedTooltipAction?: React.ComponentProps<typeof RestrictedButton>['tooltipAction'];
  variant?: React.ComponentPropsWithoutRef<typeof Button>['variant'];
}

export interface OneActionFooterProps extends FooterBaseProps {
  children?: never;
  primaryAction: ModalActionProps;
  secondaryAction?: never;
  tertiaryAction?: never;
  isBar?: boolean;
  leftText?: string;
}

export interface MultiActionFooterProps extends FooterBaseProps {
  testId?: string;
  children?: never;
  primaryAction: ModalActionProps;
  secondaryAction?: ModalActionProps;
  tertiaryAction?: ModalActionProps;
  isBar?: never;
  leftText?: never;
}

export interface MultiActionFooterPropsWithLeftText extends FooterBaseProps {
  testId?: string;
  children?: never;
  primaryAction: ModalActionProps;
  secondaryAction?: ModalActionProps;
  tertiaryAction?: never;
  isBar?: never;
  leftText?: string;
}

export type ActionFooterProps =
  | OneActionFooterProps
  | MultiActionFooterProps
  | MultiActionFooterPropsWithLeftText;

export const ModalFooterActions: React.FC<ActionFooterProps> = ({
  primaryAction,
  secondaryAction,
  tertiaryAction,
  isBar,
  leftText,
  children,
  testId = 'modal-footer-actions',
  ...rest
}) => {
  const hasLeftContent = !!leftText || !!tertiaryAction;
  return (
    <ModalFooter data-component="ModalFooterActions" testId={testId} {...rest}>
      {isBar ? (
        <Flex column alignItems="stretch" style={{ width: '100%' }}>
          <ActionButton
            testId="primary-button"
            variant="fill-primary"
            justifyContent="center"
            {...primaryAction}
          />
        </Flex>
      ) : (
        <Flex justifyContent={hasLeftContent ? 'space-between' : 'flex-end'}>
          {tertiaryAction && (
            <Box>
              <ActionButton testId="tertiary-button" variant="minimal" {...tertiaryAction} />
            </Box>
          )}
          {leftText && (
            <Text appearance="bodyMedium" color="secondary" tag="span">
              {leftText}
            </Text>
          )}
          <Flex>
            {secondaryAction && (
              <ActionButton testId="secondary-button" variant="minimal" {...secondaryAction} />
            )}
            <Box paddingLeft={16}>
              <ActionButton testId="primary-button" variant="fill-primary" {...primaryAction} />
            </Box>
          </Flex>
        </Flex>
      )}
    </ModalFooter>
  );
};

interface ActionButtonProps
  extends ModalActionProps,
    Pick<React.ComponentProps<typeof Button>, 'testId' | 'justifyContent'> {}

const ActionButton = ({
  restricted,
  variant,
  action,
  disabled,
  disabledTooltip,
  restrictedTooltip,
  restrictedTooltipAction,
  testId,
  text,
  justifyContent,
}: ActionButtonProps) => {
  const buttonProps = {
    'data-component': 'ActionButton',
    variant,
    onClick: action,
    disabled,
    testId,
    justifyContent,
  };

  return restricted ? (
    <RestrictedButton
      {...buttonProps}
      tooltip={restrictedTooltip}
      tooltipAction={restrictedTooltipAction}
    >
      {text}
    </RestrictedButton>
  ) : disabled && disabledTooltip ? (
    <Tooltip tooltipContent={disabledTooltip} placement="top">
      <Button {...buttonProps}> {text} </Button>
    </Tooltip>
  ) : (
    <Button {...buttonProps}> {text} </Button>
  );
};

const ModalWithStatics = Modal as typeof Modal & {
  Body: typeof ModalBody;
  Header: typeof ModalHeader;
  Footer: typeof ModalFooter;
  FooterActions: typeof ModalFooterActions;
  FooterHelpText: typeof ModalFooterHelpText;
};

ModalWithStatics.Body = ModalBody;
ModalWithStatics.Header = ModalHeader;
ModalWithStatics.Footer = ModalFooter;
ModalWithStatics.FooterActions = ModalFooterActions;
ModalWithStatics.FooterHelpText = ModalFooterHelpText;

export { ModalWithStatics as Modal };
